import { Link } from 'react-router-dom'

export default function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-950 to-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l7-4 7 4v14" />
              </svg>
            </div>
            <span className="text-xl font-semibold">Camp Cloud</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-400 hover:text-white transition">Features</a>
            <a href="#pricing" className="text-gray-400 hover:text-white transition">Pricing</a>
            <Link to="/platform" className="text-gray-400 hover:text-white transition">Platform Login</Link>
          </nav>
          
          <Link to="/platform" className="btn-primary">
            Get Started
          </Link>
        </div>
      </header>
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-sm mb-8">
            <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" />
            Now with Stripe Connect for instant payments
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
            Camp Registration
            <span className="block text-brand-400">Made Simple</span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-12">
            The all-in-one platform for clubs, gyms, and organizations to manage 
            youth programs. Multi-tenant, fully customizable, enterprise-ready.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/platform" className="btn-primary text-lg px-8 py-3">
              Start Free Trial
            </Link>
            <a href="#demo" className="btn-secondary text-lg px-8 py-3">
              View Demo
            </a>
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section id="features" className="py-20 px-6 bg-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Everything You Need</h2>
          <p className="text-gray-400 text-center mb-16 max-w-2xl mx-auto">
            Built on top of our open-source foundation, Camp Cloud adds multi-tenant
            capabilities, billing, and enterprise features.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 stagger-children">
            {[
              {
                icon: '🏢',
                title: 'Multi-Tenant',
                description: 'Each organization gets their own subdomain or custom domain with isolated data.',
              },
              {
                icon: '💳',
                title: 'Stripe Connect',
                description: 'Instant payment processing. Funds go directly to your connected account.',
              },
              {
                icon: '🎨',
                title: 'White Label',
                description: 'Custom branding, colors, and logos. Your brand, your experience.',
              },
              {
                icon: '👨‍👩‍👧‍👦',
                title: 'Multi-Child',
                description: 'Register multiple children with sibling discounts in a single transaction.',
              },
              {
                icon: '📊',
                title: 'Analytics',
                description: 'Track registrations, revenue, and capacity across all your camps.',
              },
              {
                icon: '🔐',
                title: 'Role-Based Access',
                description: 'Owners, admins, and viewers with granular permissions.',
              },
            ].map((feature, i) => (
              <div key={i} className="card p-6 hover:border-brand-500/50 transition-colors">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Pricing */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Simple Pricing</h2>
          <p className="text-gray-400 text-center mb-16">Start free, upgrade as you grow</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Free',
                price: '$0',
                description: 'Perfect for getting started',
                features: ['1 camp', '50 registrations/month', '1 admin', 'Subdomain only'],
                cta: 'Start Free',
              },
              {
                name: 'Starter',
                price: '$29',
                description: 'For growing organizations',
                features: ['5 camps', '500 registrations/month', '3 admins', 'Custom domain', 'Email support'],
                cta: 'Start Trial',
                popular: true,
              },
              {
                name: 'Pro',
                price: '$99',
                description: 'For established programs',
                features: ['20 camps', '2000 registrations/month', '10 admins', 'White label', 'API access', 'Priority support'],
                cta: 'Contact Sales',
              },
            ].map((plan, i) => (
              <div 
                key={i} 
                className={`card p-8 relative ${plan.popular ? 'border-brand-500 ring-2 ring-brand-500/20' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-500 text-white text-sm rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-gray-400 text-sm mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <svg className="w-5 h-5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className={plan.popular ? 'btn-primary w-full' : 'btn-secondary w-full'}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-brand-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l7-4 7 4v14" />
              </svg>
            </div>
            <span className="font-medium">Camp Cloud</span>
          </div>
          
          <p className="text-gray-500 text-sm">
            Built on <a href="https://github.com/your-org/open-camp" className="text-brand-400 hover:underline">Open Camp</a> (MIT License)
          </p>
          
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="mailto:hello@cloudcamp.uk" className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}


